import React, { useState, useRef, useEffect, useCallback } from "react";

const QRAlignmentModal = ({
  isOpen,
  onClose,
  onComplete,
  src1, // First QR image
  src2, // Second QR image
  qrBoxSize = 200, // Fixed size for each QR box
  spacing = 20, // Space between QR boxes
  initialAlignment = null,
  canvasWidth = 440, // qrBoxSize * 2 + spacing
  canvasHeight = 300, // Increased height to accommodate text below QRs
  backgroundColor = "#FFFFFF",
}) => {
  const [qr1State, setQr1State] = useState(() => ({
    scale: initialAlignment?.qr1?.scale ?? 1,
    offsetX: initialAlignment?.qr1?.offsetX ?? 0,
    offsetY: initialAlignment?.qr1?.offsetY ?? 0,
  }));

  const [qr2State, setQr2State] = useState(() => ({
    scale: initialAlignment?.qr2?.scale ?? 1,
    offsetX: initialAlignment?.qr2?.offsetX ?? 0,
    offsetY: initialAlignment?.qr2?.offsetY ?? 0,
  }));

  const [selectedQR, setSelectedQR] = useState(1);
  const [qr1Loaded, setQr1Loaded] = useState(false);
  const [qr2Loaded, setQr2Loaded] = useState(false);
  const [qr1Dimensions, setQr1Dimensions] = useState({ width: 0, height: 0 });
  const [qr2Dimensions, setQr2Dimensions] = useState({ width: 0, height: 0 });

  const qr1Ref = useRef(null);
  const qr2Ref = useRef(null);
  const containerRef = useRef(null);

  const calculateOptimalScale = useCallback(
    (imgWidth, imgHeight) => {
      const scaleX = qrBoxSize / imgWidth;
      const scaleY = qrBoxSize / imgHeight;
      return Math.min(scaleX, scaleY) * 0.9; // 90% of box size for some padding
    },
    [qrBoxSize]
  );

  // Auto-fit QRs when loaded
  useEffect(() => {
    if (qr1Loaded && qr1Dimensions.width && qr1Dimensions.height) {
      // Only auto-fit if no saved alignment data exists
      if (!initialAlignment?.qr1) {
        const optimalScale = calculateOptimalScale(
          qr1Dimensions.width,
          qr1Dimensions.height
        );
        setQr1State((prev) => ({
          ...prev,
          scale: optimalScale,
          offsetX: 0,
          offsetY: 0,
        }));
      }
      // If we have saved data, it's already set in useState initialization
    }
  }, [qr1Loaded, qr1Dimensions, initialAlignment?.qr1, calculateOptimalScale]);

  useEffect(() => {
    if (qr2Loaded && qr2Dimensions.width && qr2Dimensions.height) {
      // Only auto-fit if no saved alignment data exists
      if (!initialAlignment?.qr2) {
        const optimalScale = calculateOptimalScale(
          qr2Dimensions.width,
          qr2Dimensions.height
        );
        setQr2State((prev) => ({
          ...prev,
          scale: optimalScale,
          offsetX: 0,
          offsetY: 0,
        }));
      }
    }
  }, [qr2Loaded, qr2Dimensions, initialAlignment?.qr2, calculateOptimalScale]);

  const handleImageLoad = (qrNumber) => (e) => {
    const img = e.target;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    if (qrNumber === 1) {
      setQr1Dimensions({ width: naturalWidth, height: naturalHeight });
      setQr1Loaded(true);
    } else {
      setQr2Dimensions({ width: naturalWidth, height: naturalHeight });
      setQr2Loaded(true);
    }
  };

  const handleScaleChange = (e) => {
    const newScale = parseFloat(e.target.value);
    if (selectedQR === 1) {
      setQr1State((prev) => ({ ...prev, scale: newScale }));
    } else if (selectedQR === 2) {
      setQr2State((prev) => ({ ...prev, scale: newScale }));
    }
  };

  const handleOffsetXChange = (e) => {
    const newOffset = parseFloat(e.target.value);
    if (selectedQR === 1) {
      setQr1State((prev) => ({ ...prev, offsetX: newOffset }));
    } else if (selectedQR === 2) {
      setQr2State((prev) => ({ ...prev, offsetX: newOffset }));
    }
  };

  const handleOffsetYChange = (e) => {
    const newOffset = parseFloat(e.target.value);
    if (selectedQR === 1) {
      setQr1State((prev) => ({ ...prev, offsetY: newOffset }));
    } else if (selectedQR === 2) {
      setQr2State((prev) => ({ ...prev, offsetY: newOffset }));
    }
  };

  const centerQR = () => {
    if (selectedQR === 1) {
      setQr1State((prev) => ({ ...prev, offsetX: 0, offsetY: 0 }));
    } else {
      setQr2State((prev) => ({ ...prev, offsetX: 0, offsetY: 0 }));
    }
  };

  const autoFitQR = () => {
    if (selectedQR === 1 && qr1Dimensions.width && qr1Dimensions.height) {
      const optimalScale = calculateOptimalScale(
        qr1Dimensions.width,
        qr1Dimensions.height
      );
      setQr1State((prev) => ({
        ...prev,
        scale: optimalScale,
        offsetX: 0,
        offsetY: 0,
      }));
    } else if (
      selectedQR === 2 &&
      qr2Dimensions.width &&
      qr2Dimensions.height
    ) {
      const optimalScale = calculateOptimalScale(
        qr2Dimensions.width,
        qr2Dimensions.height
      );
      setQr2State((prev) => ({
        ...prev,
        scale: optimalScale,
        offsetX: 0,
        offsetY: 0,
      }));
    }
  };

  const matchSizes = () => {
    if (selectedQR === 1 && qr1State.scale) {
      setQr2State((prev) => ({ ...prev, scale: qr1State.scale }));
    } else if (selectedQR === 2 && qr2State.scale) {
      setQr1State((prev) => ({ ...prev, scale: qr2State.scale }));
    }
  };

  const getCurrentState = () => (selectedQR === 1 ? qr1State : qr2State);

  const handleSave = () => {
    if (!qr1Ref.current || (!qr1Loaded && !qr2Loaded)) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Fill with background color
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    const drawQR = (
      imgSrc,
      state,
      dimensions,
      boxX,
      placeholder = false,
      isLeftSide = true
    ) => {
      return new Promise((resolve) => {
        if (!imgSrc || placeholder) {
          // Draw placeholder if no image
          ctx.fillStyle = "#f3f4f6";
          ctx.fillRect(boxX, 20, qrBoxSize, qrBoxSize);
          ctx.fillStyle = "#9ca3af";
          ctx.font = "14px Arial";
          ctx.textAlign = "center";
          ctx.fillText(
            "QR Not Available",
            boxX + qrBoxSize / 2,
            20 + qrBoxSize / 2
          );
          resolve();
          return;
        }

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          // Save the current canvas state
          ctx.save();

          // Calculate the clipping area based on which side this QR is on
          if (isLeftSide) {
            // For left QR: clip to left half of canvas
            ctx.beginPath();
            ctx.rect(0, 0, canvasWidth / 2, canvasHeight);
            ctx.clip();
          } else {
            // For right QR: clip to right half of canvas
            ctx.beginPath();
            ctx.rect(canvasWidth / 2, 0, canvasWidth / 2, canvasHeight);
            ctx.clip();
          }

          const scaledWidth = dimensions.width * state.scale;
          const scaledHeight = dimensions.height * state.scale;

          // Calculate position - for left QR, center in left half; for right QR, center in right half
          let qrX, qrY;

          if (isLeftSide) {
            // Center in left half of canvas
            qrX = canvasWidth / 4 - scaledWidth / 2 + state.offsetX;
          } else {
            // Center in right half of canvas
            qrX = (3 * canvasWidth) / 4 - scaledWidth / 2 + state.offsetX;
          }

          qrY = 20 + qrBoxSize / 2 - scaledHeight / 2 + state.offsetY;

          ctx.drawImage(img, qrX, qrY, scaledWidth, scaledHeight);

          // Restore the canvas state (removes clipping)
          ctx.restore();
          resolve();
        };
        img.onerror = () => {
          // Draw error placeholder
          ctx.fillStyle = "#fef2f2";
          ctx.fillRect(boxX, 20, qrBoxSize, qrBoxSize);
          ctx.fillStyle = "#dc2626";
          ctx.font = "12px Arial";
          ctx.textAlign = "center";
          ctx.fillText(
            "Failed to Load",
            boxX + qrBoxSize / 2,
            20 + qrBoxSize / 2
          );
          resolve();
        };
        img.src = imgSrc;
      });
    };

    const boxPadding = 10;
    const qr1BoxX = boxPadding;
    const qr2BoxX = canvasWidth - qrBoxSize - boxPadding;

    Promise.all([
      drawQR(src1, qr1State, qr1Dimensions, qr1BoxX, !qr1Loaded, true), // isLeftSide = true
      drawQR(src2, qr2State, qr2Dimensions, qr2BoxX, !qr2Loaded, false), // isLeftSide = false
    ]).then(() => {
      const dataURL = canvas.toDataURL("image/png");

      // Save alignment data along with the image
      const alignmentData = {
        qr1: qr1State,
        qr2: qr2State,
        canvasWidth,
        canvasHeight,
        lastAligned: new Date().toISOString(),
      };

      onComplete(dataURL, alignmentData);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-tier-primary dark:bg-tier-primary-dark border dark:border-gray-600 p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto scrollable">
        <h3 className="text-lg font-semibold mb-4 text-center text-white">
          QR Code Box Alignment
        </h3>

        {initialAlignment && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center space-x-2">
              <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-sm">
                info
              </span>
              <span className="text-sm text-blue-800 dark:text-blue-200">
                Using saved alignment settings from{" "}
                {new Date(initialAlignment.lastAligned).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        {/* QR Selection Tabs */}
        <div className="flex justify-center mb-4">
          <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setSelectedQR(1)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedQR === 1
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow"
                  : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              QR Code 1 (Left)
            </button>
            <button
              onClick={() => setSelectedQR(2)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedQR === 2
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow"
                  : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              QR Code 2 (Right)
            </button>
          </div>
        </div>

        {/* Preview Container with Boxes */}
        <div className="flex justify-center mb-4">
          <div
            ref={containerRef}
            className="relative select-none overflow-hidden"
            style={{
              width: Math.min(canvasWidth, 600),
              height: Math.min(canvasHeight, 400),
              backgroundColor: backgroundColor,
              border: "2px solid #e5e7eb",
            }}
          >
            {/* Left QR Section - cropped on the left side, includes text area */}
            <div
              className="absolute overflow-hidden"
              style={{
                left: 0, // Start from left edge for cropping
                top: 0,
                width: "50%", // Take half the container width
                height: "100%",
              }}
              onClick={() => setSelectedQR(1)}
            >
              {src1 && (
                <img
                  ref={qr1Ref}
                  src={src1}
                  alt="QR Code 1"
                  onLoad={handleImageLoad(1)}
                  onError={(e) => console.error("Failed to load QR1:", e)}
                  className={`absolute ${
                    selectedQR === 1 ? "ring-2 ring-teal-400" : ""
                  }`}
                  style={{
                    left: "50%",
                    top: "40%",
                    transform: `translate(calc(-50% + ${
                      qr1State.offsetX *
                      (Math.min(canvasWidth, 600) / canvasWidth)
                    }px), calc(-50% + ${
                      qr1State.offsetY *
                      (Math.min(canvasHeight, 400) / canvasHeight)
                    }px)) scale(${qr1State.scale})`,
                    transformOrigin: "center",
                    maxWidth: "none",
                    maxHeight: "none",
                    display: qr1Loaded ? "block" : "none",
                    zIndex: 1,
                  }}
                />
              )}
              {!qr1Loaded && src1 && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">
                  Loading QR 1...
                </div>
              )}

              {/* QR Box 1 with guides on top */}
              <div
                className={`absolute border-2 border-dashed z-10 ${
                  selectedQR === 1
                    ? "border-teal-500 bg-teal-50/20"
                    : "border-gray-400"
                }`}
                style={{
                  left: 10, // Fixed padding from left edge
                  top: 10, // Position from top to leave space for title
                  width: qrBoxSize * (Math.min(canvasWidth, 600) / canvasWidth),
                  height:
                    qrBoxSize * (Math.min(canvasHeight, 400) / canvasHeight),
                }}
              >
                {/* Center guidelines on top of the image */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-red-500 opacity-60 transform -translate-x-1/2 z-10 pointer-events-none"></div>
                <div className="absolute top-1/2 left-0 right-0 h-px bg-red-500 opacity-60 transform -translate-y-1/2 z-10 pointer-events-none"></div>
              </div>
            </div>

            {/* Right QR Section - cropped on the right side, includes text area */}
            <div
              className="absolute overflow-hidden"
              style={{
                left: "50%",
                top: 0,
                width: "50%",
                height: "100%",
              }}
              onClick={() => setSelectedQR(2)}
            >
              {src2 && (
                <img
                  ref={qr2Ref}
                  src={src2}
                  alt="QR Code 2"
                  onLoad={handleImageLoad(2)}
                  onError={(e) => console.error("Failed to load QR2:", e)}
                  className={`absolute ${
                    selectedQR === 2 ? "ring-2 ring-teal-400" : ""
                  }`}
                  style={{
                    left: "50%",
                    top: "40%",
                    transform: `translate(calc(-50% + ${
                      qr2State.offsetX *
                      (Math.min(canvasWidth, 600) / canvasWidth)
                    }px), calc(-50% + ${
                      qr2State.offsetY *
                      (Math.min(canvasHeight, 400) / canvasHeight)
                    }px)) scale(${qr2State.scale})`,
                    transformOrigin: "center",
                    maxWidth: "none",
                    maxHeight: "none",
                    display: qr2Loaded ? "block" : "none",
                    zIndex: 1,
                  }}
                />
              )}

              {/* QR Box 2 with guides on top */}
              <div
                className={`absolute border-2 border-dashed z-10 ${
                  selectedQR === 2
                    ? "border-teal-500 bg-teal-50/20"
                    : "border-gray-400"
                }`}
                style={{
                  right: 10, // Fixed padding from right edge within the section
                  top: 10, // Position from top to leave space for title
                  width: qrBoxSize * (Math.min(canvasWidth, 600) / canvasWidth),
                  height:
                    qrBoxSize * (Math.min(canvasHeight, 400) / canvasHeight),
                }}
              >
                {!qr2Loaded && src2 && (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">
                    Loading QR 2...
                  </div>
                )}

                {/* Center guidelines on top of the image */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-red-500 opacity-60 transform -translate-x-1/2 z-10 pointer-events-none"></div>
                <div className="absolute top-1/2 left-0 right-0 h-px bg-red-500 opacity-60 transform -translate-y-1/2 z-10 pointer-events-none"></div>
              </div>
            </div>

            {/* Center dividing line between boxes */}
            <div
              className="absolute top-0 bottom-0 w-px bg-gray-300 opacity-50 z-20 pointer-events-none"
              style={{
                left: "50%",
                transform: "translateX(-50%)",
              }}
            ></div>
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-4 mb-4">
          <div className="text-center text-sm font-medium text-gray-300">
            Controls for QR Code {selectedQR}{" "}
            {selectedQR === 1 ? "(Left Box)" : "(Right Box)"}
          </div>

          {/* Scale Control */}
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-300 min-w-fit">
              Scale:
            </label>
            <input
              type="range"
              min="0.1"
              max="2"
              step="0.01"
              value={getCurrentState().scale}
              onChange={handleScaleChange}
              className="flex-1"
            />
            <span className="text-sm text-gray-400 min-w-fit">
              {Math.round(getCurrentState().scale * 100)}%
            </span>
          </div>

          {/* Position Controls */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-300 min-w-fit">
                X Offset:
              </label>
              <input
                type="range"
                min={-qrBoxSize / 4}
                max={qrBoxSize / 4}
                step="1"
                value={getCurrentState().offsetX}
                onChange={handleOffsetXChange}
                className="flex-1"
              />
              <span className="text-xs text-gray-400 min-w-fit">
                {getCurrentState().offsetX}px
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-300 min-w-fit">
                Y Offset:
              </label>
              <input
                type="range"
                min={-qrBoxSize / 4}
                max={qrBoxSize / 4}
                step="1"
                value={getCurrentState().offsetY}
                onChange={handleOffsetYChange}
                className="flex-1"
              />
              <span className="text-xs text-gray-400 min-w-fit">
                {getCurrentState().offsetY}px
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-2">
            <button
              onClick={autoFitQR}
              className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            >
              Auto Fit QR {selectedQR}
            </button>
            <button
              onClick={centerQR}
              className="px-3 py-1 text-sm bg-teal-500 text-white rounded hover:bg-teal-600 transition-colors"
            >
              Center QR {selectedQR}
            </button>
            <button
              onClick={matchSizes}
              className="px-3 py-1 text-sm bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
            >
              Match Size to QR {selectedQR}
            </button>
            {initialAlignment && (
              <button
                onClick={() => {
                  // Reset to initial auto-fit values
                  if (
                    selectedQR === 1 &&
                    qr1Dimensions.width &&
                    qr1Dimensions.height
                  ) {
                    const optimalScale = calculateOptimalScale(
                      qr1Dimensions.width,
                      qr1Dimensions.height
                    );
                    setQr1State({
                      scale: optimalScale,
                      offsetX: 0,
                      offsetY: 0,
                    });
                  } else if (
                    selectedQR === 2 &&
                    qr2Dimensions.width &&
                    qr2Dimensions.height
                  ) {
                    const optimalScale = calculateOptimalScale(
                      qr2Dimensions.width,
                      qr2Dimensions.height
                    );
                    setQr2State({
                      scale: optimalScale,
                      offsetX: 0,
                      offsetY: 0,
                    });
                  }
                }}
                className="px-3 py-1 text-sm bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
              >
                Reset to Default
              </button>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="text-xs text-gray-400 mb-4 text-center space-y-1">
          <p>
            Each QR code fits within its own {qrBoxSize}×{qrBoxSize}px box with
            additional space below for text
          </p>
          <p>
            Red guidelines show the center of each box • Use Auto Fit to
            optimize QR size • Use Center to reset position
          </p>
          <p>
            Output size: {canvasWidth}×{canvasHeight}px (includes 60px space
            below each QR for text)
          </p>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-action-white">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!qr1Loaded || !qr2Loaded}
            className="btn-action"
          >
            Save Aligned QRs
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRAlignmentModal;
